import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from 'antd';
import { FiPlus, FiAlertCircle, FiLink, FiLock } from 'react-icons/fi';
import { Button } from '../common/Button';

export const PatientIdentityForm = ({
  selectedPatient,
  isCreatingNew,
  setIsCreatingNew,
  patForm,
  setPatForm,
  fieldErrors,
  patientList,
  onSearch,
  isLoadingSearch,
  onSelectPatient,
  onChangePatient,
  uniquePatientIds,
  patientMap,
  targetPatientId,
  setTargetPatientId,
  setSearchTerm,
  searchTerm = '',
  onSwitchPatient,
  lockPatientIdentity = false,
}: any) => {
  const { t } = useTranslation('NewCaseWizard');

  return (
    <div className={`rounded-xl border-2 p-1 transition-colors ${selectedPatient ? 'border-green-400/30 bg-green-50/20 dark:border-green-700/50 dark:bg-green-900/10' : 'border-slate-100 bg-slate-50 dark:bg-gray-700/30 dark:border-gray-600'}`}>
      
      {targetPatientId && !selectedPatient && (
        <div className="mx-4 mt-3 mb-1 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 overflow-hidden">
                <FiLink className="text-blue-500 flex-shrink-0" />
                <span className="text-xs text-blue-700 dark:text-blue-300 truncate">
                    {t('oneStep.patient.assigningFilesForId')}{' '}
                    <strong>{targetPatientId}</strong>
                </span>
            </div>
            <div className="text-[10px] text-blue-400 uppercase font-bold tracking-wider">
                {t('oneStep.patient.currentCase')}
            </div>
        </div>
      )}

      {!lockPatientIdentity && !selectedPatient && !isCreatingNew && (
        <div className="p-4 space-y-4">
          <Select
            showSearch
            allowClear
            placeholder={`${t('oneStep.patient.searchFor')} ${targetPatientId || t('oneStep.patient.patient')}...`}
            className="w-full"
            size="large"
            filterOption={false}
            onSearch={onSearch}
            onClear={() => setSearchTerm?.('')}
            onDropdownVisibleChange={(open) => {
              if (
                open &&
                targetPatientId &&
                searchTerm === targetPatientId &&
                (!patientList?.items || patientList.items.length === 0)
              ) {
                setSearchTerm?.('');
              }
            }}
            loading={isLoadingSearch}
            onChange={onSelectPatient}
            notFoundContent={
              isLoadingSearch
                ? t('oneStep.patient.searching', 'Searching...')
                : t('oneStep.patient.noPatientsFound', 'No patients found')
            }
            options={patientList?.items?.map((p: any) => ({
              label: `${p.patient_name || ''} ${p.patient_last_name || ''} (${p.patient_id})`.trim(),
              value: p.id,
            }))}
            style={{ width: '100%' }}
          />
          <Button className="w-full border-dashed dark:border-gray-500 dark:text-gray-300 dark:hover:text-white" variant="secondary" onClick={() => setIsCreatingNew(true)}>
            <FiPlus className="mr-2"/> {t('oneStep.patient.createNewPatient')}
          </Button>
        </div>
      )}

      {!lockPatientIdentity && isCreatingNew && !selectedPatient && (
        <div className="p-4 space-y-3 bg-white dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-gray-400">{t('oneStep.patient.newPatientAutoFilled')}</span>
            <button
              type="button"
              onClick={() => {
                setIsCreatingNew(false);
                setSearchTerm?.('');
              }}
              className="text-xs text-red-500 hover:underline"
            >
              {t('oneStep.patient.searchExisting', 'Search existing patient')}
            </button>
          </div>
          
          {/* Conflict dropdown */}
          {uniquePatientIds.length > 1 && (
            <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded">
              <div className="text-[10px] font-bold text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1"><FiAlertCircle/> {t('oneStep.patient.multipleIdsFoundSelectSource')}</div>
              <Select 
                className="w-full" 
                size="small" 
                value={targetPatientId || patForm.id} 
                onChange={(val) => {
                    if (onSwitchPatient) {
                        onSwitchPatient(val);
                    } else {
                        setTargetPatientId(val); 
                        setSearchTerm(val); 
                    }
                }} 
                options={uniquePatientIds.map((id:string) => ({ label: `${patientMap[id] || t('oneStep.patient.unknownName')} (${id})`, value: id }))} 
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Input placeholder={t('oneStep.patient.firstName')} value={patForm.firstName} onChange={e => setPatForm({...patForm, firstName: e.target.value})} status={fieldErrors['patient_name'] ? 'error' : ''} className="dark:bg-gray-700 dark:text-white dark:border-gray-600" />
              {fieldErrors['patient_name'] && <div className="text-[10px] text-red-500 mt-1">{fieldErrors['patient_name']}</div>}
            </div>
            <div>
              <Input placeholder={t('oneStep.patient.lastName')} value={patForm.lastName} onChange={e => setPatForm({...patForm, lastName: e.target.value})} status={fieldErrors['patient_last_name'] ? 'error' : ''} className="dark:bg-gray-700 dark:text-white dark:border-gray-600" />
              {fieldErrors['patient_last_name'] && <div className="text-[10px] text-red-500 mt-1">{fieldErrors['patient_last_name']}</div>}
            </div>
          </div>
          <div>
            <Input placeholder={t('oneStep.patient.mrnId')} value={patForm.id} onChange={e => setPatForm({...patForm, id: e.target.value})} status={fieldErrors['patient_id'] ? 'error' : ''} className="dark:bg-gray-700 dark:text-white dark:border-gray-600" />
            {fieldErrors['patient_id'] && <div className="text-[10px] text-red-500 mt-1">{fieldErrors['patient_id']}</div>}
          </div>
        </div>
      )}

      {selectedPatient && (
        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg flex justify-between items-center gap-3 shadow-sm">
          <div className="min-w-0">
            <h4 className="font-bold text-slate-800 dark:text-gray-200">
              {selectedPatient.patient_name} {selectedPatient.patient_last_name}
            </h4>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-gray-400">
                ID: {selectedPatient.patient_id}
              </span>
              {targetPatientId && targetPatientId === selectedPatient.patient_id && (
                <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-bold">
                  {t('oneStep.patient.matchesFile')}
                </span>
              )}
            </div>
            {lockPatientIdentity && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-gray-400">
                <FiLock className="shrink-0 text-slate-400" aria-hidden />
                {t('oneStep.patient.lockedToProfile')}
              </div>
            )}
          </div>
          {!lockPatientIdentity && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onChangePatient}
              className="dark:text-gray-400 dark:hover:text-white shrink-0"
            >
              {t('oneStep.patient.change')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};